import { T, useTheme, dens, Ico, Card, Eyebrow, Btn, Badge, StatusBadge } from '../../../design';
import type { Claim } from '../../../types/claims.types';
import { fmtTimestampDate } from '../../../utils';
import { TYPE_LABEL } from '../claimsConfig';
import { Thumb, ClaimsError } from './shared';

export interface ClaimsListProps {
  claims: Claim[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onOpen: (claim: Claim) => void;
  onNew: () => void;
}

export function ClaimsList({ claims, loading, error, onRetry, onOpen, onNew }: ClaimsListProps) {
  const t = useTheme();
  const d = dens(t);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap }}>
      <Btn full size="lg" icon={<Ico.plus size={20} />} onClick={onNew}>Ny reklamation</Btn>

      {error ? (
        <ClaimsError onRetry={onRetry} message={error} />
      ) : loading ? (
        [0, 1, 2].map((i) => <Card key={i} pad={16} style={{ height: 96, background: `linear-gradient(90deg,${T.tertiary} 25%, ${T.surface} 50%, ${T.tertiary} 75%)`, backgroundSize: '300% 100%', animation: 'pp-shimmer 1.3s linear infinite', border: 'none' }} />)
      ) : claims.length === 0 ? (
        <Card pad={28} style={{ textAlign: 'center', marginTop: 8 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: T.accentLight, color: t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><Ico.receipt size={26} /></div>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.fg }}>Ingen reklamationer endnu</div>
          <div style={{ fontSize: 13, color: T.fg3, marginTop: 6, lineHeight: 1.5 }}>Opret en reklamation når en vare er defekt, beskadiget eller forkert leveret.</div>
        </Card>
      ) : (
        <>
          <Eyebrow style={{ padding: '2px 2px 0' }}>Seneste reklamationer · {claims.length}</Eyebrow>
          {claims.map((c) => {
            const first = c.items[0];
            const more = c.items.length - 1;
            const media = c.items.reduce((s, i) => s + (i.media || 0), 0);
            return (
              <Card key={c.id} pad={0} onClick={() => onOpen(c)} style={{ cursor: 'pointer', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderBottom: `1px solid ${T.borderLight}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <StatusBadge status={c.status} />
                    <span style={{ fontSize: 11.5, color: T.fg3, fontWeight: 600, fontFamily: 'ui-monospace, monospace' }}>#{c.id}</span>
                  </div>
                  <span style={{ fontSize: 11.5, color: T.fg3, fontWeight: 600 }}>{fmtTimestampDate(c.createdAt)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 13, padding: 14 }}>
                  <Thumb size={46} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{first.productName}{more > 0 && <span style={{ color: T.fg3, fontWeight: 600 }}> +{more} flere</span>}</div>
                    <div style={{ fontSize: 12, color: T.fg3, fontWeight: 600, marginTop: 3, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span>{TYPE_LABEL[first.claimType]}</span>
                      {media > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Ico.image size={12} />{media}</span>}
                    </div>
                  </div>
                  <Badge tone={c.preferredOutcome === 'credited' ? 'accent' : 'info'}>{c.preferredOutcome === 'credited' ? 'Kreditering' : 'Erstatning'}</Badge>
                  <span style={{ color: T.fg3, flexShrink: 0 }}><Ico.chevR size={18} /></span>
                </div>
              </Card>
            );
          })}
        </>
      )}
    </div>
  );
}
